import { EventType } from '../dtos/analytics.dto';
import { UserDto } from '../dtos/user.dto';
import { Customer, ServiceDto } from '../dtos/customer.dto';
import mixpanel from 'mixpanel-browser';
import { toCamelCase } from '../utils/string-normalization';
import notificationService from './notification.service';

const APP_NAME = 'Sane-Portal';
const INITIALIZATION_FAILED_MESSAGE = 'Mixpanel initialization failed';

// TODO: need to map type of extraInfo with the eventName\error event.
class AnalyticsService {

    private isInitialized = false;

    constructor() {
        if (!this.isLocal()) {
            this.initMixpanel();
        }
    }

    initMixpanel() {
        try {
            mixpanel.init(window.CONFIG?.MIX_PANEL, {
                ignore_dnt: true, ip: true,
                loaded: (mixpanelInstance) => {
                    if (this.validateToken(mixpanelInstance.get_config('token'))) {
                        console.log('Mixpanel initialized successfully');
                        this.isInitialized = true;
                    }
                },
            });

            setTimeout(() => {
                if (!this.isInitialized) {
                    console.error('Mixpanel initialization timed out');
                    void notificationService.sendMixpanelAlert(INITIALIZATION_FAILED_MESSAGE);
                }
            }, 5000);
        } catch (e) {
            console.error(INITIALIZATION_FAILED_MESSAGE, e);
            void notificationService.sendMixpanelAlert(INITIALIZATION_FAILED_MESSAGE);
        }
    }

    private isLocal() {
        return window.CONFIG?.ENVIRONMENT_NAME === 'Local';
    }

    private isDemoEnv() {
        return window.CONFIG?.ENVIRONMENT_NAME?.toLowerCase() === 'demo';
    }

    private extractAttuidFromUserId(userId: string): string | undefined {
        if (!userId) return undefined;
        return userId.split('@')[0];
    }

    public registerUser(user: UserDto) {
        // Register attuid as super property only in demo env
        if (this.isDemoEnv() && user?.id) {
            const attuid = this.extractAttuidFromUserId(user.id);
            if (attuid) {
                mixpanel.register({ attuid });
            }
        }
    }

    public identifyUser(userId: string) {
        mixpanel.identify(userId);
    }

    public resetUser() {
        try {
            mixpanel.reset();
        } catch { console.error('failed reset mixpanel'); }
    }

    private validateToken(token: string) {
        return token?.length && token === window.CONFIG?.MIX_PANEL;
    }

    public sendEvent = (eventType: EventType, user?: UserDto, customer?: Customer, location?: ServiceDto,
        extraInfo: any = {}, cb?: () => void): void => {
        const { customerId, customerName } = customer || {};
        if (this.isLocal() || !this.isInitialized) {
            cb?.();
        } else {
            try {
                mixpanel.track(eventType,
                    {
                        systemEnv: toCamelCase(window.CONFIG.ENVIRONMENT_NAME),
                        sourceApp: APP_NAME,
                        customerId,
                        customerName,
                        ...location,
                        is_external: !!user?.allowedCustomers && user?.source === 'CUSTOMER',
                        logLevel: 'info',
                        ...extraInfo,
                    }, this.handleTrackResult, cb);
            } catch (e) {
                console.error('mixpanel event failed', e);
                cb?.();
            }
        }
    };

    private handleTrackResult(response: any) {
        if (response !== 1) { // failure
            void notificationService.sendMixpanelAlert(`Mixpanel track failed: ${response}.`);
        }
    }
}
const analyticsService = new AnalyticsService();
export default analyticsService;